import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Button,
} from '@mui/material';
import {
  Search,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';

interface OrderItem {
  id: string;
  checklist_id: number;
  project_id: number;
  customer_id: number;
  customer_name: string;
  customer_phone?: string;
  project_title: string;
  order_number?: string;
  project_status?: string;
  category: string;
  category_key: string;
  description: string;
  supplier: string;
  ordered_date: string;
  expected_delivery?: string;
  fit_date?: string;
}

export default function OnOrder() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [showAll, setShowAll] = useState(false);
  const [sortField, setSortField] = useState<string>('ordered_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/orderformchecklist/on-order', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data || []);
      } else {
        console.error('Failed to load orders:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper to check if order is past stock due date
  const isPastDue = (order: OrderItem) => {
    if (!order.expected_delivery) return false;
    const dueDate = new Date(order.expected_delivery);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Filter to current orders (not past due) unless showAll is true
  const currentOrders = showAll ? orders : orders.filter(order => !isPastDue(order));

  // Get unique suppliers for filter
  const uniqueSuppliers = Array.from(new Set(currentOrders.map(o => o.supplier).filter(Boolean))).sort();

  // Get unique categories for filter
  const uniqueCategories = Array.from(new Set(currentOrders.map(o => o.category))).sort();

  const filteredOrders = currentOrders.filter(order => {
    const matchesSearch =
      (order.customer_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (order.order_number?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (order.supplier?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (order.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === 'all' || order.category === categoryFilter;
    const matchesSupplier = supplierFilter === 'all' || order.supplier === supplierFilter;

    return matchesSearch && matchesCategory && matchesSupplier;
  }).sort((a, b) => {
    if (!sortField) return 0;

    let aVal: any = '';
    let bVal: any = '';

    switch (sortField) {
      case 'supplier':
        aVal = (a.supplier || '').toLowerCase();
        bVal = (b.supplier || '').toLowerCase();
        break;
      case 'customer_name':
        aVal = (a.customer_name || '').toLowerCase();
        bVal = (b.customer_name || '').toLowerCase();
        break;
      case 'order_number':
        aVal = (a.order_number || '').toLowerCase();
        bVal = (b.order_number || '').toLowerCase();
        break;
      case 'category':
        aVal = a.category.toLowerCase();
        bVal = b.category.toLowerCase();
        break;
      case 'ordered_date':
        aVal = a.ordered_date ? new Date(a.ordered_date).getTime() : 0;
        bVal = b.ordered_date ? new Date(b.ordered_date).getTime() : 0;
        break;
      case 'expected_delivery':
        aVal = a.expected_delivery ? new Date(a.expected_delivery).getTime() : 0;
        bVal = b.expected_delivery ? new Date(b.expected_delivery).getTime() : 0;
        break;
      case 'fit_date':
        aVal = a.fit_date ? new Date(a.fit_date).getTime() : 0;
        bVal = b.fit_date ? new Date(b.fit_date).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: { backgroundColor: string; color: string } } = {
      'Doors': { backgroundColor: '#2196f3', color: '#fff' },
      'Worktops': { backgroundColor: '#9c27b0', color: '#fff' },
      'Sinks & Taps': { backgroundColor: '#00bcd4', color: '#fff' },
      'PJH Appliances': { backgroundColor: '#ff9800', color: '#fff' },
      'Dancia Handles': { backgroundColor: '#4caf50', color: '#fff' },
      'PBC Cabinets': { backgroundColor: '#f44336', color: '#fff' },
    };
    return colors[category] || { backgroundColor: '#9e9e9e', color: '#fff' };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (order: OrderItem) => {
    if (order.project_id) {
      navigate(`/projects/${order.project_id}`);
    }
  };

  // Count by category
  const categoryCounts = uniqueCategories.reduce((acc, cat) => {
    acc[cat] = currentOrders.filter(o => o.category === cat).length;
    return acc;
  }, {} as { [key: string]: number });

  // Count past due orders
  const pastDueCount = orders.filter(o => isPastDue(o)).length;

  return (
    <Box>
      {/* Category Summary Boxes */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(6, 1fr)'
        },
        gap: { xs: 1, sm: 1, md: 1 },
        mb: 2.5
      }}>
        {uniqueCategories.map(category => (
          <Card
            key={category}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              borderLeft: `4px solid ${getCategoryColor(category).backgroundColor}`
            }}
            onClick={() => setCategoryFilter(categoryFilter === category ? 'all' : category)}
          >
            <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
              <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
                {category}
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' } }}>
                {categoryCounts[category] || 0}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' }
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' } }}>
              <TextField
                fullWidth
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 20%' } }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {uniqueCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 20%' } }}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={supplierFilter}
                  label="Supplier"
                  onChange={(e) => setSupplierFilter(e.target.value)}
                >
                  <MenuItem value="all">All Suppliers</MenuItem>
                  {uniqueSuppliers.map(sup => (
                    <MenuItem key={sup} value={sup}>{sup}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color="textSecondary">
                {filteredOrders.length} items on order
              </Typography>
              <Button
                variant={showAll ? "contained" : "outlined"}
                size="small"
                color={showAll ? "primary" : "inherit"}
                onClick={() => {
                  setShowAll(!showAll);
                  setPage(0);
                }}
              >
                {showAll ? "Hide" : "Show"} Past Due ({pastDueCount})
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 600, sm: 750, md: 900 } }}>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort('category')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Category
                  {sortField === 'category' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('supplier')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Supplier
                  {sortField === 'supplier' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('order_number')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Order #
                  {sortField === 'order_number' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('customer_name')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' }, display: { xs: 'none', sm: 'table-cell' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Customer
                  {sortField === 'customer_name' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('ordered_date')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Ordered
                  {sortField === 'ordered_date' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('expected_delivery')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' }, display: { xs: 'none', md: 'table-cell' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Stock Due
                  {sortField === 'expected_delivery' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('fit_date')}
                sx={{ cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '16px' }, display: { xs: 'none', md: 'table-cell' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Fit Date
                  {sortField === 'fit_date' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow
                  key={order.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => handleRowClick(order)}
                >
                  <TableCell>
                    <Chip
                      label={order.category}
                      size="small"
                      sx={getCategoryColor(order.category)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {order.supplier || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.order_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {order.customer_name || '-'}
                  </TableCell>
                  <TableCell>
                    {formatDate(order.ordered_date)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {formatDate(order.expected_delivery)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {formatDate(order.fit_date)}
                  </TableCell>
                </TableRow>
              ))}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    {loading ? 'Loading...' : 'No items on order. Items will appear here when order dates are set in project checklists.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}
